import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import blogService from '../services/BlogService';

/**
 * BlogSection Component - Master blog page showing list of all posts
 * 
 * Features:
 * - Displays all blog posts in chronological order
 * - Search functionality
 * - Tag filtering
 * - Responsive design with post preview cards
 */
const BlogSection = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [blogStats, setBlogStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load blog data on component mount
  useEffect(() => {
    const loadBlogData = async () => {
      try {
        setIsLoading(true);
        
        // Load posts and metadata
        const allPosts = await blogService.getAllPosts();
        const tags = await blogService.getAllTags();
        const stats = await blogService.getBlogStats();
        
        setPosts(allPosts);
        setFilteredPosts(allPosts);
        setAllTags(tags);
        setBlogStats(stats);
        
        setError(null);
      } catch (err) {
        console.error('Error loading blog data:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadBlogData();
  }, []);

  // Handle search and filtering
  useEffect(() => {
    const applyFilters = async () => {
      let filtered = posts;

      // Apply search filter
      if (searchQuery.trim()) {
        filtered = await blogService.searchPosts(searchQuery);
      }

      // Apply tag filter
      if (selectedTag) {
        filtered = filtered.filter(post => 
          post.tags && post.tags.includes(selectedTag)
        );
      }

      setFilteredPosts(filtered);
    };

    applyFilters();
  }, [searchQuery, selectedTag, posts]);

  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(selectedTag === tag ? '' : tag);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTag('');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="bg-red-100 border border-red-300 rounded-lg p-6 text-center">
            <h2 className="text-xl font-bold text-red-800 mb-2">Error Loading Blog</h2>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">📝 LazyMagpie Blog</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Insights, updates, and thoughts on decentralized gaming, blockchain technology, 
              and the future of peer-to-peer entertainment.
            </p>
            
            {/* Blog Stats */}
            {blogStats && (
              <div className="flex justify-center items-center space-x-6 mt-4 text-sm text-gray-500">
                <span>{blogStats.totalPosts} posts</span>
                <span>•</span>
                <span>{blogStats.totalTags} topics</span>
                {blogStats.featuredPosts > 0 && (
                  <>
                    <span>•</span>
                    <span>{blogStats.featuredPosts} featured</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Search and Filter Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Clear Filters */}
            {(searchQuery || selectedTag) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Filter by topic:</h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagClick(tag)}
                    className={`px-3 py-1 text-sm rounded-full transition ${
                      selectedTag === tag
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="mt-4 text-sm text-gray-500">
            {filteredPosts.length === posts.length ? (
              `Showing all ${posts.length} posts`
            ) : (
              `Showing ${filteredPosts.length} of ${posts.length} posts`
            )}
          </div>
        </div>

        {/* Blog Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📝</div>
            <h3 className="text-xl font-medium text-gray-600 mb-2">No posts found</h3>
            <p className="text-gray-500">
              {searchQuery || selectedTag 
                ? 'Try adjusting your search or filter criteria.'
                : 'Check back later for new content!'
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-8">
            {filteredPosts.map((post, index) => (
              <article
                key={post.slug}
                className={`bg-white rounded-lg shadow-sm hover:shadow-md transition cursor-pointer ${
                  post.featured ? 'ring-2 ring-blue-200' : ''
                }`}
                onClick={() => handlePostClick(post.slug)}
              >
                <div className="p-6">
                  {/* Post Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {post.featured && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            ⭐ Featured
                          </span>
                        )}
                        <span className="text-sm text-gray-500">
                          {blogService.formatDate(post.date, 'short')}
                        </span>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-gray-800 hover:text-blue-600 transition">
                        {post.title}
                      </h2>
                      
                      <p className="text-gray-600 mt-2 line-clamp-3">
                        {post.description}
                      </p>
                    </div>
                  </div>

                  {/* Post Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {post.tags.map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Post Footer */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span>📖 {blogService.calculateReadingTime(post.description)} min read</span>
                    </div>
                    
                    <div className="text-blue-600 hover:text-blue-700 font-medium">
                      Read more →
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Navigation Footer */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            ← Back to GameMaker
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogSection;